import { useCallback, useEffect, useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import ReactTooltip from "react-tooltip";
import { useNavigate } from "react-router-dom";
import Header from "../../components/Header";
import PageContainer from "../../components/PageContainer";
import TeamRoleBadge from "../../components/teamRoles/TeamRoleBadge";
import FirmFlowTable from "../../components/mytasks/FirmFlowTable";
import Sidebar from "../../components/Sidebar";
import { usePermissions } from "../../context/PermissionsContext";
import { getTeamRoles } from "../../services/teams/getTeamRoles";
import { getTeams } from "../../services/teams/getTeams";
import TeamRolesModal from "./TeamRolesModal";
import {
    getTeamRoleColor,
    getTeamRoleName,
    normalizeTeamRoles,
} from "./teamMembersUtils";

const TeamsListPage = () => {
    const { can, permissionsLoading } = usePermissions();
    const navigate = useNavigate();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [teams, setTeams] = useState([]);
    const [teamRoles, setTeamRoles] = useState([]);
    const [isRolesModalOpen, setIsRolesModalOpen] = useState(false);
    //const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const noPermissionMessage = "You don't have permission to do this.";
    const canReadTeams = can("teams.read") || can("teams.write") || can("teams.delete");
    const canWriteTeams = can("teams.write");
    const canDeleteTeams = !permissionsLoading && can("teams.delete") && can("teams.read");
    const canReadTeamRoles =
        can("team_roles.read") ||
        can("team_roles.write") ||
        can("team_roles.delete");
    const canWriteTeamRoles = can("team_roles.write");
    const canDeleteTeamRoles = can("team_roles.delete");

    const loadTeams = useCallback(
        async (showLoader = true, refreshedRoles = null) => {
            if (permissionsLoading) return false;
            if (showLoader) setLoading(true);
            const [teamsResponse, rolesResponse] = await Promise.all([
                canReadTeams ? getTeams() : Promise.resolve(null),
                canReadTeamRoles && !refreshedRoles
                    ? getTeamRoles()
                    : Promise.resolve(null),
            ]);
            if (teamsResponse?.success) {
                setTeams(teamsResponse?.data?.teams || []);
            } else if (canReadTeams) {
                console.log(teamsResponse);
            } else {
                setTeams([]);
            }
            if (!canReadTeamRoles) {
                setTeamRoles([]);
            } else if (refreshedRoles) {
                setTeamRoles(refreshedRoles);
            } else if (
                rolesResponse?.success &&
                rolesResponse?.data?.success !== false
            ) {
                setTeamRoles(normalizeTeamRoles(rolesResponse?.data?.teamRoles));
            } else if (rolesResponse?.status === 403) {
                toast.error(noPermissionMessage);
            }
            setLoading(false);
            return !canReadTeams || teamsResponse?.success === true;
        },
        [canReadTeamRoles, canReadTeams, permissionsLoading]
    );

    useEffect(() => {
        loadTeams();
    }, [loadTeams]);

    const handleRolesChanged = useCallback(async (refreshedRoles) => {
        if (!canReadTeamRoles) return;
        if (refreshedRoles) setTeamRoles(refreshedRoles);
        const refreshed = await loadTeams(false, refreshedRoles);
        if (!refreshed) {
            toast.error("Team roles changed, but teams could not be refreshed.");
        }
    }, [canReadTeamRoles, loadTeams]);

    /* const onClickDeleteTeam = (team) => {
        setTeamToDelete(team);
        setIsDeleteModalOpen(true);
    }; */

    /* const removeTeam = async () => {
        const response = await deleteTeam(teamToDelete.id);
        if (response.data.success) {
            const newTeams = teams.filter((team) => team.id !== teamToDelete.id);
            setTeams(newTeams);
            toast.success("Team deleted successfully");
        } else {
            toast.error(response.data.message);
        }
        setIsDeleteModalOpen(false);
    };
 */
    return (
        <div className="flex h-screen overflow-hidden">
            <Toaster />
            <ReactTooltip
                type="light"
                effect="solid"
                place="top"
                border={true}
                borderColor={"rgb(220,220,220)"}
                className="rounded-md bg-gray-50"
                multiline={false}
            />
            <TeamRolesModal
                open={isRolesModalOpen}
                setOpen={setIsRolesModalOpen}
                canRead={canReadTeamRoles}
                canWrite={canWriteTeamRoles}
                canDelete={canDeleteTeamRoles}
                onRolesChanged={handleRolesChanged}
            />
            {/*  <DeleteModal
                isModalOpen={isDeleteModalOpen}
                setIsModalOpen={setIsDeleteModalOpen}
                onRemove={removeTeam}
                title={
                    teamToDelete &&
                    `Are you sure you want to delete the team ${teamToDelete.name}`
                }
            /> */}
            {/* Sidebar */}
            <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
            {/* Content area */}
            <div className="relative flex flex-col flex-1 overflow-x-hidden overflow-y-auto">
                {/*  Site header */}
                <Header sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

                <PageContainer
                    isLoading={loading}
                    loadingText="Loading teams..."
                >
                    <main>
                        <div className="w-full px-4 py-8 mx-auto sm:px-6 lg:px-8 max-w-9xl">
                            {/* Page header */}
                            <div className="mb-8 sm:flex sm:justify-between sm:items-center">
                                {/* Left: Title */}
                                <div className="mb-4 sm:mb-0">
                                    <h1 className="text-2xl font-bold md:text-3xl text-slate-800">
                                        Teams
                                    </h1>
                                </div>

                                <div className="grid items-end justify-start grid-flow-col gap-2 sm:auto-cols-max sm:justify-end">
                                    <span
                                        data-tip={
                                            canReadTeamRoles
                                                ? "Manage Team Roles"
                                                : noPermissionMessage
                                        }
                                        className="inline-flex"
                                    >
                                        <button
                                            type="button"
                                            onClick={() => setIsRolesModalOpen(true)}
                                            disabled={!canReadTeamRoles}
                                            className={`h-10 text-white bg-slate-600 btn ${
                                                canReadTeamRoles
                                                    ? "hover:bg-slate-700"
                                                    : "opacity-50 cursor-not-allowed"
                                            }`}
                                        >
                                            Manage Roles
                                        </button>
                                    </span>
                                    <span
                                        data-tip={
                                            canWriteTeams
                                                ? "Add Team"
                                                : noPermissionMessage
                                        }
                                        className="inline-flex"
                                    >
                                        <button
                                            onClick={() => navigate("/teams/create")}
                                            disabled={!canWriteTeams}
                                            className={`h-10 text-white bg-teal-500 btn ${
                                                canWriteTeams
                                                    ? "hover:bg-teal-600"
                                                    : "opacity-50 cursor-not-allowed"
                                            }`}
                                        >
                                            <svg
                                                className="w-4 h-4 opacity-50 fill-current shrink-0"
                                                viewBox="0 0 16 16"
                                            >
                                                <path d="M15 7H9V1c0-.6-.4-1-1-1S7 .4 7 1v6H1c-.6 0-1 .4-1 1s.4 1 1 1h6v6c0 .6.4 1 1 1s1-.4 1-1V9h6c.6 0 1-.4 1-1s-.4-1-1-1z" />
                                            </svg>
                                            <span className="ml-2 xs:block">
                                                Add Team
                                            </span>
                                        </button>
                                    </span>
                                </div>
                            </div>

                            {canReadTeams ? (
                                <FirmFlowTable
                                columns={[
                                    {
                                        Header: "Name",
                                        accessor: "name",
                                        Cell: (props) => props.value,
                                    },
                                    {
                                        Header: "Department",
                                        accessor: "departmentName",
                                        Cell: (props) => props.value,
                                    },
                                    {
                                        Header: "Team Members",
                                        accessor: "members",
                                        Cell: (props) =>
                                            props.value.map((member) => (
                                                <ul
                                                    key={member.id}
                                                    className="list-disc"
                                                >
                                                    <li className="flex items-center gap-2">
                                                        <span>{member.name}</span>
                                                        <span aria-hidden="true">-</span>
                                                        <TeamRoleBadge
                                                            name={getTeamRoleName(
                                                                member,
                                                                teamRoles,
                                                            )}
                                                            colorHex={getTeamRoleColor(
                                                                member,
                                                                teamRoles,
                                                            )}
                                                            className="text-xs"
                                                        />
                                                    </li>
                                                </ul>
                                            )),
                                    },
                                    {
                                        Header: "Actions",
                                        accessor: "id",
                                        Cell: (props) => (
                                            <>
                                                <span
                                                    data-tip={
                                                        canWriteTeams
                                                            ? "Edit"
                                                            : noPermissionMessage
                                                    }
                                                    className="inline-flex"
                                                >
                                                    <button
                                                        onClick={() =>
                                                            navigate(
                                                                `/teams/${props.value}`
                                                            )
                                                        }
                                                        disabled={!canWriteTeams}
                                                        className={`w-20 h-10 mx-2 text-white bg-teal-500 btn ${
                                                            canWriteTeams
                                                                ? "hover:bg-teal-600"
                                                                : "opacity-50 cursor-not-allowed"
                                                        }`}
                                                    >
                                                        Edit
                                                    </button>
                                                </span>
                                                {canDeleteTeams && <span
                                                    data-tip={
                                                        canDeleteTeams
                                                            ? "Delete"
                                                            : noPermissionMessage
                                                    }
                                                    className="inline-flex"
                                                >
                                                    <button
                                                        onClick={() =>
                                                            navigate(
                                                                `/teams/delete/${props.value}`
                                                            )
                                                        }
                                                        disabled={!canDeleteTeams}
                                                        className={`w-20 h-10 mx-2 text-white bg-red-500 btn ${
                                                            canDeleteTeams
                                                                ? "hover:bg-red-600"
                                                                : "opacity-50 cursor-not-allowed"
                                                        }`}
                                                    >
                                                        Delete
                                                    </button>
                                                </span>}
                                            </>
                                        ),
                                    },
                                ]}
                                data={teams}
                                />
                            ) : (
                                <div className="p-4 text-sm border rounded-sm border-slate-200 text-slate-600">
                                    You don't have permission to view teams.
                                </div>
                            )}
                        </div>
                    </main>
                </PageContainer>
            </div>
        </div>
    );
};
export default TeamsListPage;
